import { useDebounceFn, useResizeObserver } from '@vueuse/core'
import animateScrollTo from 'animated-scroll-to'
import { computed, ref, Ref, ShallowRef, watch } from 'vue'

export const useCarousel = (
  itemsContainer: Readonly<ShallowRef<HTMLDivElement | null>>,
  itemsTotal: Ref<number>,
  itemsVisible: Ref<number>,
  initialPage: number = 0,
  itemsGap: number = 16 /* gap-4 */
) => {
  const currentPage = ref(initialPage)
  const totalPages = computed(() => Math.ceil(itemsTotal.value / itemsVisible.value))
  const isFirst = computed(() => currentPage.value === 0)
  const isLast = computed(() => currentPage.value === totalPages.value - 1)

  const itemStyle = computed(() => {
    const itemsCount = totalPages.value === 1 ? itemsTotal.value : itemsVisible.value
    return {
      flexBasis: `calc((100% - ${itemsGap}px * ${itemsCount - 1}) / ${itemsCount})`,
      marginLeft: `${itemsGap}px`
    }
  })

  const adjustScrollX = () => {
    if (itemsContainer.value === null) {
      return
    }

    const x = currentPage.value * (itemsContainer.value.clientWidth + itemsGap)
    animateScrollTo([x, null], {
      elementToScroll: itemsContainer.value,
      speed: 200
    })
  }

  const resizeHandler = useDebounceFn(adjustScrollX, 300)
  useResizeObserver(document.body, resizeHandler)

  watch(
    itemsVisible,
    () => {
      if (totalPages.value > 0) {
        currentPage.value = Math.min(currentPage.value, totalPages.value - 1)
      }
    },
    { immediate: true }
  )

  watch(itemsTotal, (currentValue, oldValue) => {
    if (currentValue < oldValue) {
      currentPage.value = initialPage
    }
  })

  watch(
    [currentPage, itemsContainer],
    () => {
      adjustScrollX()
    },
    { immediate: true }
  )

  const goOnPage = (n: number) => {
    currentPage.value = n
  }

  const next = () => {
    if (!isLast.value) {
      currentPage.value += 1
    }
  }

  const prev = () => {
    if (!isFirst.value) {
      currentPage.value -= 1
    }
  }

  return { next, prev, goOnPage, isFirst, isLast, currentPage, totalPages, itemsGap, itemStyle }
}
